import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  useInvitationStore,
  usePendingInvitations,
  usePendingCount,
  PendingInvitation,
} from "@/stores/invitation/invitation-store";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { toast } from "sonner";
import {
  Mail,
  Check,
  X,
  ChevronRight,
  ChevronLeft,
  Building2,
  Clock,
  UserPlus,
  Loader2,
  Home,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getInvitationAcceptRedirect } from "@/utils/invitationRedirect";

/**
 * PendingInvitationsBanner
 *
 * Displays a prominent banner on the dashboard when the user has pending
 * organization invitations. Allows quick accept/decline actions directly
 * from the dashboard without navigating away.
 */
export const PendingInvitationsBanner: React.FC = () => {
  const navigate = useNavigate();
  const pendingInvitations = usePendingInvitations();
  const pendingCount = usePendingCount();
  const { fetchPendingInvitations, acceptInvitation, declineInvitation, isLoading, error } =
    useInvitationStore();
  const { fetchOrganizations } = useOrganizationStore();

  // Track which invitation is currently being processed
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Carousel state for multiple invitations
  const [currentIndex, setCurrentIndex] = useState(0);

  // Dismissed state (per session)
  const [isDismissed, setIsDismissed] = useState(false);

  // Fetch invitations on mount
  useEffect(() => {
    fetchPendingInvitations();
  }, [fetchPendingInvitations]);

  // Reset carousel index if invitations change
  useEffect(() => {
    if (currentIndex >= pendingCount && pendingCount > 0) {
      setCurrentIndex(Math.max(0, pendingCount - 1));
    }
  }, [pendingCount, currentIndex]);

  // Handle accept invitation
  const handleAccept = async (invitation: PendingInvitation) => {
    try {
      setProcessingId(invitation.membershipId);

      const result = await acceptInvitation(invitation.membershipId);

      toast.success(`You've joined ${result.organization?.name ?? invitation.organization.name}!`, {
        description: `You now have ${result.role ?? invitation.role} access to this organization.`,
      });

      // Refresh organizations list to include the new one
      await fetchOrganizations();

      navigate(getInvitationAcceptRedirect(result));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to accept invitation";
      toast.error("Failed to accept invitation", {
        description: message,
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Handle decline invitation
  const handleDecline = async (invitation: PendingInvitation) => {
    try {
      setProcessingId(invitation.membershipId);

      await declineInvitation(invitation.membershipId);

      toast.success("Invitation declined", {
        description: `You've declined the invitation to ${invitation.organization.name}.`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to decline invitation";
      toast.error("Failed to decline invitation", {
        description: message,
      });
    } finally {
      setProcessingId(null);
    }
  };

  // Navigate carousel
  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : pendingCount - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < pendingCount - 1 ? prev + 1 : 0));
  };

  // Format time until expiry
  const formatTimeUntilExpiry = (expiresAt: string): string => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry.getTime() - now.getTime();

    if (diff <= 0) return "Expired";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 1) return `${days} days left`;
    if (days === 1) return `1 day left`;
    if (hours > 1) return `${hours} hours left`;
    return "Expires soon";
  };

  // Get role badge color
  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "outline" => {
    switch (role) {
      case "admin":
        return "default";
      case "member":
        return "secondary";
      default:
        return "outline";
    }
  };

  // Don't render if no invitations, dismissed, or loading initially
  if (pendingCount === 0 || isDismissed) {
    return null;
  }

  const currentInvitation = pendingInvitations[currentIndex];
  if (!currentInvitation) {
    return null;
  }

  const isProcessing = processingId === currentInvitation.membershipId;

  return (
    <Card className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border-primary/20 mb-6">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className="hidden sm:flex h-12 w-12 rounded-full bg-primary/20 items-center justify-center shrink-0">
            <Mail className="h-6 w-6 text-primary" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header with count */}
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground">
                {pendingCount === 1
                  ? "You have a pending invitation"
                  : `You have ${pendingCount} pending invitations`}
              </h3>
              {pendingCount > 1 && (
                <Badge variant="secondary" className="text-xs">
                  {currentIndex + 1} of {pendingCount}
                </Badge>
              )}
            </div>

            {/* Invitation details */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {/* Organization */}
              <div className="flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5" />
                <span className="font-medium text-foreground">
                  {currentInvitation.organization.name}
                </span>
              </div>

              {/* Role */}
              <Badge variant={getRoleBadgeVariant(currentInvitation.role)} className="text-xs">
                {currentInvitation.role}
              </Badge>

              {/* Inviter */}
              <div className="flex items-center gap-1">
                <UserPlus className="h-3.5 w-3.5" />
                <span>from {currentInvitation.inviter?.name ?? "a team member"}</span>
              </div>

              {currentInvitation.provisioningConfig?.homeProjectId && (
                <div className="flex items-center gap-1">
                  <Home className="h-3.5 w-3.5" />
                  <span>Homepage pre-configured</span>
                </div>
              )}

              {/* Expiry */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 text-[hsl(var(--feedback-warning))]">
                      <Clock className="h-3.5 w-3.5" />
                      <span>{formatTimeUntilExpiry(currentInvitation.expiresAt)}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      Expires: {new Date(currentInvitation.expiresAt).toLocaleDateString()}{" "}
                      {new Date(currentInvitation.expiresAt).toLocaleTimeString()}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Message if present */}
            {currentInvitation.message && (
              <p className="text-sm text-muted-foreground mt-1 italic truncate">
                "{currentInvitation.message}"
              </p>
            )}
          </div>

          {/* Navigation buttons for multiple invitations */}
          {pendingCount > 1 && (
            <div className="hidden md:flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handlePrevious}
                disabled={isProcessing}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleNext}
                disabled={isProcessing}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDecline(currentInvitation)}
                    disabled={isProcessing || isLoading}
                    className="border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <X className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline ml-1">Decline</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Decline invitation</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    onClick={() => handleAccept(currentInvitation)}
                    disabled={isProcessing || isLoading}
                  >
                    {isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                    <span className="ml-1">Accept</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Join {currentInvitation.organization.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Dismiss button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground shrink-0"
                  onClick={() => setIsDismissed(true)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Dismiss for now</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Mobile navigation for multiple invitations */}
        {pendingCount > 1 && (
          <div className="flex md:hidden items-center justify-center gap-2 mt-3 pt-3 border-t border-border/50">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrevious}
              disabled={isProcessing}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              {currentIndex + 1} / {pendingCount}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleNext}
              disabled={isProcessing}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="mt-2 text-sm text-destructive">
            {error}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
